import { useMemo, useReducer, useState } from 'react';

import useNotice from '@/notices/hooks/useNotice';
import RipplingDataExtractor from '@/shared/api/ripplingDataExtractor';
import { parseAPIError } from '@/shared/redux/helpers/parseApiError';
import Button from '@/shared/ui/Button';
import FontIcon from '@/shared/ui/FontIcon';

import { Badge, Code, Loader, MultiSelect, Paper, TextInput } from '@mantine/core';

import '@/shared/dev/styles/RipplingDataExtractorPanel.scss';

type Status = 'idle' | 'running' | 'success' | 'failed';

type EntityState = {
  status: Status;
  ranAt?: number;
  message?: string;
};

type Action =
  | { type: 'start'; key: string }
  | { type: 'success'; key: string; message?: string }
  | { type: 'fail'; key: string; message: string };

const reducer = (state: Record<string, EntityState>, action: Action): Record<string, EntityState> => {
  switch (action.type) {
    case 'start':
      return { ...state, [action.key]: { status: 'running' } };
    case 'success':
      return { ...state, [action.key]: { status: 'success', ranAt: Date.now(), message: action.message } };
    case 'fail':
      return { ...state, [action.key]: { status: 'failed', ranAt: Date.now(), message: action.message } };
  }
};

const ENTITIES = [
  'accounts',
  'users',
  'usermeta',
  'locations',
  'positions',
  'positionusers',
  'sites',
  'places',
  'shifts',
  'availabilities',
  'blocks',
  'requests',
  'requesttypes',
  'settings',
  'shiftbreak',
  'templates',
  'templateshifts',
  'time',
  'timeoffbalances',
  'timeoffpolicies',
  'timeoffpoliciesrates',
  'timeoffledger',
  'conversations',
  'messages',
] as const;

const BULK_ALL_KEY = '__all__';
const BULK_SELECTED_KEY = '__selected__';

export function RipplingDataExtractorPanel() {
  const { errorNotice } = useNotice();
  const [statuses, dispatch] = useReducer(reducer, {});
  const [jobKey, setJobKey] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [lastResponse, setLastResponse] = useState<unknown>(null);

  const entityOptions = useMemo(() => ENTITIES.map(e => ({ value: e, label: e })), []);

  const buildBody = () => (jobKey.trim() ? { job_key: jobKey.trim() } : {});

  const post = async (key: string, path: string, body?: object) => {
    dispatch({ type: 'start', key });
    try {
      const res = await RipplingDataExtractor.url(path).post(body ?? buildBody());
      setLastResponse(res);
      dispatch({ type: 'success', key });
      return res;
    } catch (err: unknown) {
      const parsed = parseAPIError(err);
      const message = parsed.message || 'Request failed';
      setLastResponse(err);
      dispatch({ type: 'fail', key, message });
      errorNotice(`Extraction failed: ${message}`);
      throw err;
    }
  };

  const extractEntity = (entity: string) => () => post(entity, `/${entity}`);

  const extractSelected = () => {
    if (selected.length === 0) {
      errorNotice('Pick at least one entity to extract.');
      return;
    }
    return post(BULK_SELECTED_KEY, '/entities', { ...buildBody(), entities: selected });
  };

  const extractAll = () => post(BULK_ALL_KEY, '/all');

  const isAnyRunning = Object.values(statuses).some(s => s.status === 'running');

  return (
    <div className="rde-panel">
      <p className="rde-panel__hint">
        Posts to <Code>{CONFIG.RIPPLING_DATA_EXTRACTOR_ROOT}</Code>. Uses your current WIW auth token; extracts run
        against the account you're logged into.
      </p>

      <TextInput
        className="rde-panel__job-key"
        label="Job key (optional)"
        description="Defaults to a unix timestamp if blank."
        placeholder="auto"
        value={jobKey}
        onChange={e => setJobKey(e.currentTarget.value)}
      />

      <Paper className="rde-panel__bulk" shadow="xs" radius="md" p="md" withBorder>
        <h4>Bulk</h4>

        <MultiSelect
          className="rde-panel__multiselect"
          data={entityOptions}
          value={selected}
          onChange={setSelected}
          placeholder="Pick entities to extract"
          searchable
          clearable
        />

        <div className="rde-panel__bulk-actions">
          <Button color="primary" onClick={extractSelected} disabled={isAnyRunning} size="sm">
            <StatusGlyph status={statuses[BULK_SELECTED_KEY]?.status} /> Extract selected
          </Button>
          <Button color="secondary" onClick={extractAll} disabled={isAnyRunning} size="sm">
            <StatusGlyph status={statuses[BULK_ALL_KEY]?.status} /> Extract all
          </Button>
        </div>
      </Paper>

      <h4 className="rde-panel__entities-header">Per-entity</h4>
      <div className="rde-panel__entities">
        {ENTITIES.map(entity => {
          const state = statuses[entity];
          return (
            <div key={entity} className="rde-panel__entity">
              <span className="rde-panel__entity-name">{entity}</span>
              <EntityStatus state={state} />
              <Button
                color="secondary"
                size="sm"
                onClick={extractEntity(entity)}
                disabled={state?.status === 'running'}
              >
                <StatusGlyph status={state?.status} /> Extract
              </Button>
            </div>
          );
        })}
      </div>

      {lastResponse !== null && (
        <div className="rde-panel__last-response">
          <h4>Last response</h4>
          <Code block>{JSON.stringify(lastResponse, null, 2)}</Code>
        </div>
      )}
    </div>
  );
}

function StatusGlyph({ status }: { status?: Status }) {
  if (status === 'running') {
    return <Loader size="xs" />;
  }

  if (status === 'success') {
    return <FontIcon icon="checkmark-circled" />;
  }

  if (status === 'failed') {
    return <FontIcon icon="warning" />;
  }

  return null;
}

function EntityStatus({ state }: { state?: EntityState }) {
  if (!state || state.status === 'idle') {
    return <span className="rde-panel__entity-status" />;
  }

  const color = state.status === 'success' ? 'green' : state.status === 'failed' ? 'red' : 'gray';
  const label = state.status === 'running' ? 'running' : state.ranAt ? formatTime(state.ranAt) : state.status;

  return (
    <Badge className="rde-panel__entity-status" color={color} variant="light" size="sm" title={state.message}>
      {label}
    </Badge>
  );
}

function formatTime(ts: number) {
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
}
